<template>
  <Block>
    <div
      class="grid grid-cols-1 sm:grid-cols-2 px-6 py-4 gap-y-4 sm:gap-y-3 sm:gap-x-0"
      :class="boostEnabled ? 'xl:grid-cols-4' : 'xl:grid-cols-3'"
    >
      <!-- Today -->
      <BriefBarItem
        label="Aujourd'hui"
        :value="`${dailyTarget?.current ?? 0} / ${$t('roadmap.sales', dailyTarget?.target ?? 0)}`"
        :value-class="todayCompleted ? 'text-green-700' : 'text-gray-900'"
        :subtitle="sepaText(dailyTarget?.leadsWaitingForConfirmation)"
        cell-class="sm:pr-4 sm:border-r border-gray-100"
      />

      <!-- Yesterday -->
      <BriefBarItem
        label="Hier"
        :value="`${yesterdayPerformance?.current ?? 0} / ${$t('roadmap.sales', yesterdayPerformance?.target ?? 0)}`"
        :value-class="yesterdayCompleted ? 'text-green-700' : 'text-gray-900'"
        :subtitle="sepaText(yesterdayPerformance?.leadsWaitingForConfirmation)"
        cell-class="sm:pl-4 xl:border-r border-gray-100 xl:pr-4"
      />

      <!-- Month -->
      <BriefBarItem
        label="Ce mois"
        :label-suffix="`· ${formatPercentage(monthlyProgression)}`"
        :badge-text="monthlyStatusLabel"
        :badge-class="monthlyStatusClass"
        :value="`${monthlyPerformance?.current ?? 0} / ${$t('roadmap.sales', monthlyPerformance?.target ?? 0)}`"
        :subtitle="sepaText(leadsWaitingForConfirmation)"
        cell-class="sm:pr-4 sm:border-r border-gray-100 sm:border-t sm:pt-3 xl:border-t-0 xl:pt-0 xl:pl-4"
      />

      <!-- Booster -->
      <BriefBarItem
        v-if="boostEnabled"
        label="Booster"
        :badge-text="
          reachedTier ? `Palier ${reachedTier.tier} (+${multiplierPercentage(reachedTier.multiplicator)}%)` : ''
        "
        :badge-class="reachedTier ? 'bg-green-50 text-green-700' : ''"
        :value="nextTier ? $t('roadmap.sales', nextTier.threshold - monthlyCurrent) : ''"
        :subtitle="nextTier ? `pour +${multiplierPercentage(nextTier.multiplicator)}%` : ''"
        cell-class="sm:pl-4 sm:border-t sm:pt-3 xl:border-t-0 xl:pt-0 xl:border-l border-gray-100"
      />
    </div>
  </Block>
</template>

<script>
import { Block, formatPercentage } from "@estaly/ui";
import { boosterThresholds, getNextTier, getReachedTier, multiplierPercentage } from "@/utils/booster";
import BriefBarItem from "@/components/home/BriefBarItem.vue";

export default {
  components: {
    Block,
    BriefBarItem,
  },
  props: {
    dailyTarget: {
      type: Object,
      default: null,
    },
    yesterdayPerformance: {
      type: Object,
      default: null,
    },
    monthlyPerformance: {
      type: Object,
      default: null,
    },
    leadsWaitingForConfirmation: {
      type: Number,
      default: 0,
    },
    boostEnabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    todayCompleted() {
      const current = this.dailyTarget?.current ?? 0;
      const target = this.dailyTarget?.target ?? 0;
      return target > 0 && current >= target;
    },
    yesterdayCompleted() {
      const current = this.yesterdayPerformance?.current ?? 0;
      const target = this.yesterdayPerformance?.target ?? 0;
      return target > 0 && current >= target;
    },
    monthlyProgression() {
      const target = this.monthlyPerformance?.target ?? 0;
      const current = this.monthlyPerformance?.current ?? 0;
      return target > 0 ? current / target : 0;
    },
    projectionProgression() {
      const target = this.monthlyPerformance?.target ?? 0;
      const projection = this.monthlyPerformance?.projection ?? 0;
      return target > 0 ? projection / target : 0;
    },
    monthlyStatusLabel() {
      if (this.projectionProgression < 0.8) return "En retard";
      if (this.projectionProgression <= 1) return "Dans les temps";
      return "En avance";
    },
    monthlyStatusClass() {
      if (this.projectionProgression < 0.8) return "bg-red-50 text-red-700";
      if (this.projectionProgression <= 1) return "bg-blue-50 text-blue-700";
      return "bg-green-50 text-green-700";
    },
    monthlyCurrent() {
      return this.monthlyPerformance?.current ?? 0;
    },
    reachedTier() {
      if (!this.boostEnabled) return null;
      return getReachedTier(this.monthlyCurrent, boosterThresholds);
    },
    nextTier() {
      if (!this.boostEnabled) return null;
      return getNextTier(this.monthlyCurrent, boosterThresholds);
    },
  },
  methods: {
    formatPercentage,
    multiplierPercentage,
    sepaText(count) {
      return count > 0 ? `+${count} SEPA en attente` : "";
    },
  },
};
</script>
